import React, { useState, useEffect } from 'react';
import { LoginForm } from './shared/LoginForm';
import { RegisterForm } from './shared/RegisterForm';
import { ForgotPasswordForm } from './shared/ForgotPasswordForm';
import { usePageTracking } from '../hooks/usePageTracking';
import './LandingPage.css';

type AuthView = 'login' | 'register' | 'forgot-password';

// Detect iOS device (iPhone, iPad, iPod)
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  
  // Check for iOS devices
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return true;
  }
  
  // Check for iPad on iOS 13+ (which reports as Mac)
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  
  return false;
};

export const LandingPage: React.FC = () => {
  const [authView, setAuthView] = useState<AuthView>('login');
  const [showMobileAuth, setShowMobileAuth] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  // Track page view
  usePageTracking('Landing Page');

  // Check for iOS on mount
  useEffect(() => {
    setIsIOS(isIOSDevice());
  }, []);

  return (
    <div className="landing-page">
      {/* Mobile Login Button */}
      <div className="mobile-login-header">
        {isIOS && (
          <a 
            href="https://apps.apple.com/us/app/unreleased-tracks/id6755366187"
            className="get-app-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get The App!
          </a>
        )}
        <button 
          className="mobile-login-button"
          onClick={() => setShowMobileAuth(true)}
        >
          Login / Register
        </button>
      </div>

      <div className="landing-container">
        {/* Left side - Site description */}
        <div className="landing-content">
          <div className="landing-header">
            <h1 className="landing-title">Unreleased</h1>
            <p className="landing-subtitle">Share Unreleased Music for Feedback.</p>
            <p className="landing-subtitle">Join a community of independent artists.</p>
            <p className="landing-subtitle">Get constructive feedback on your tracks.</p>
            <p className="landing-subtitle">Build your sound.</p>
          </div>

          <div className="landing-description">
            <section className="feature-section">
              <h2>Music Feedback Community for Independent Artists & Producers</h2>
              <p>
                Unreleased is a music feedback platform where independent artists, producers, 
                and musicians share their work in progress tracks to get constructive feedback from fellow creators.
                Join thousands of home studio musicians improving their craft through peer review.
              </p>
            </section>

            <section className="feature-section">
              <h3>How to Get Feedback on Your Music</h3>
              <div className="steps-container">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>Join Our Community</h4>
                    <p>Start your music feedback journey by creating a free account</p>
                    <button 
                      className="step-cta-button"
                      onClick={() => setShowMobileAuth(true)}
                    >
                      Sign Up Now
                    </button>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>Share Your Music and Unreleased Tracks</h4>
                    <p>Share your work-in-progress music with our feedback community</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Give Constructive Feedback</h4>
                    <p>Listen to other artists' tracks and provide thoughtful critique to earn karma</p>
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Get Constructive Feedback from Other Artists in the Community</h4>
                    <p>Get valuable insights on mixing, composition, and production quality</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="feature-section">
              <h3>Why Join Our Music Feedback Platform?</h3>
              <ul className="benefits-list">
                <li>
                  <strong>Karma-Based Feedback System:</strong> Give music feedback to get feedback. Earn karma points by providing quality music criticism, 
                  which unlocks more opportunities to share your unreleased tracks
                </li>
                <li>
                  <strong>Constructive Music Community:</strong> Connect with serious independent musicians and producers 
                  who provide thoughtful, actionable feedback on your demos
                </li>
                <li>
                  <strong>Improve Your Home Studio Production:</strong> Get honest opinions on your mixing, composition, 
                  arrangement, and overall production quality from fellow artists
                </li>
                <li>
                  <strong>Discover New Unreleased Music:</strong> Explore fresh, unreleased tracks from independent 
                  artists across all genres and connect with emerging talent
                </li>
              </ul>
            </section>

            <section className="feature-section">
              <h3>Perfect for Home Studio Musicians:</h3>
              <div className="target-audience">
                <div className="audience-item">
                  <h4>🎵 Independent Artists</h4>
                  <p>Get feedback on your demos and unreleased tracks before release</p>
                </div>
                <div className="audience-item">
                  <h4>🎛️ Music Producers & Beat Makers</h4>
                  <p>Test your beats, production techniques, and mixing skills</p>
                </div>
                <div className="audience-item">
                  <h4>🎸 Songwriters & Composers</h4>
                  <p>Refine your compositions, arrangements, and songwriting craft</p>
                </div>
                <div className="audience-item">
                  <h4>🎤 Vocalists & Recording Artists</h4>
                  <p>Get constructive feedback on vocal performances and delivery</p>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Right side - Authentication forms */}
        <div className="landing-auth">
          {isIOS && (
            <a 
              href="https://apps.apple.com/us/app/unreleased-tracks/id6755366187"
              className="get-app-button desktop-get-app"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get The App!
            </a>
          )}
          <div className="auth-container">
            <div className="auth-tabs">
              <button
                className={`auth-tab ${authView === 'login' ? 'active' : ''}`}
                onClick={() => setAuthView('login')}
              >
                Login
              </button>
              <button
                className={`auth-tab ${authView === 'register' ? 'active' : ''}`}
                onClick={() => setAuthView('register')}
              >
                Register
              </button>
            </div>

            <div className="auth-form-wrapper">
              {authView === 'login' && (
                <LoginForm 
                  onSwitchToRegister={() => setAuthView('register')} 
                  onSwitchToForgotPassword={() => setAuthView('forgot-password')}
                />
              )}
              {authView === 'register' && (
                <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
              )}
              {authView === 'forgot-password' && (
                <ForgotPasswordForm onSwitchToLogin={() => setAuthView('login')} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Auth Modal */}
      {showMobileAuth && (
        <div className="mobile-auth-modal">
          <div className="mobile-auth-overlay" onClick={() => setShowMobileAuth(false)} />
          <div className="mobile-auth-content">
            <button 
              className="mobile-auth-close"
              onClick={() => setShowMobileAuth(false)}
            >
              ×
            </button>
            
            <div className="auth-container mobile-auth-container">
              <div className="auth-tabs">
                <button
                  className={`auth-tab ${authView === 'login' ? 'active' : ''}`}
                  onClick={() => setAuthView('login')}
                >
                  Login
                </button>
                <button
                  className={`auth-tab ${authView === 'register' ? 'active' : ''}`}
                  onClick={() => setAuthView('register')}
                >
                  Register
                </button>
              </div>

              <div className="auth-form-wrapper">
                {authView === 'login' && (
                  <LoginForm 
                    onSwitchToRegister={() => setAuthView('register')} 
                    onSwitchToForgotPassword={() => setAuthView('forgot-password')}
                  />
                )}
                {authView === 'register' && (
                  <RegisterForm onSwitchToLogin={() => setAuthView('login')} />
                )}
                {authView === 'forgot-password' && (
                  <ForgotPasswordForm onSwitchToLogin={() => setAuthView('login')} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;